/**
 * Fans one per-lead operation out across a selection.
 *
 * The customer plane has no bulk endpoint — a "bulk" action is N independent
 * writes with no transaction — and its write bucket is a token bucket of 60
 * refilling at exactly one token per second (viq-backend
 * apps/customer-portal/src/middleware/rateLimit.ts, `DEFAULT_CONFIGS.write`).
 * The table keeps its selection across "Load more"
 * (`preserveSelectedRowKeys`), so a selection is not bounded by one 50-row
 * page. A `Promise.all` over 200 leads therefore 429s mid-run and, without
 * per-lead tracking, reads as a random partial success.
 *
 * Hence the four properties this file exists for: bounded concurrency, no
 * fail-fast (every lead is attempted), a 429 waited out rather than counted
 * as a failure, and — the moment the bucket runs dry — a run that PACES
 * itself to the server's one-a-second budget instead of racing it.
 *
 * That last one is the difference between a large selection succeeding
 * slowly and one failing fast. Four workers, three retries and a doubling
 * backoff give each lead about seven seconds; once ~60 writes in the bucket
 * are gone, four workers contend for one token a second on staggered wake
 * schedules, and the losers get reported as failures with nothing actually
 * wrong. Below, the first 429 switches the whole run onto a shared schedule
 * where each call reserves its own slot before waiting for it, so there are
 * no losers to mis-report.
 *
 * Pure: no fetch, no endpoint names, no shell imports (the closure-only
 * invariant for packages/*). The operation arrives as a callback.
 */

export interface BulkRunResult {
  succeeded: string[];
  /**
   * Per-lead failures. `message` is for the console log only — the operator
   * sees a count-based summary built from `CRM_COPY.bulk`, never raw error
   * text (the G6 plainness convention).
   */
  failed: { id: string; message: string }[];
}

export interface BulkRunOptions {
  /** In-flight calls BEFORE pacing engages. Once it does, the shared
   *  schedule — not this number — sets the rate. */
  concurrency?: number;
  /** Rate-limit retries per lead before it is reported as failed. */
  maxRetries?: number;
  /** Injected by tests so the backoff path doesn't actually wait. */
  sleep?: (ms: number) => Promise<void>;
  /**
   * Injected alongside `sleep`: the pacer reads the clock, so a fake sleep
   * needs a clock that its own waiting advances.
   */
  now?: () => number;
  /** Fired as each lead settles, success or failure — the bar's progress. */
  onProgress?: (done: number, total: number) => void;
}

const DEFAULT_CONCURRENCY = 4;
/**
 * Generous on purpose. A retry now waits for a slot on the paced schedule
 * rather than for a blind doubling backoff, so the old budget (3 tries, ~7
 * seconds, then "failed") was really a rule that any selection outrunning
 * the bucket reported failures. Retries that wait long enough to win a token
 * cost a few seconds each and are the honest outcome.
 */
const DEFAULT_MAX_RETRIES = 8;
const BASE_BACKOFF_MS = 1_000;
/** Ceiling on the blind doubling — reached only when the server sends no
 *  `Retry-After` and keeps refusing. */
const MAX_BACKOFF_MS = 15_000;

/** The server's write-bucket capacity: how many writes go out at full speed
 *  before pacing can possibly matter. Read by the bar to explain a slow run. */
export const SERVER_WRITE_BURST = 60;

/**
 * The gap between paced calls. The bucket refills one token per second;
 * spacing slightly wider leaves room for clock skew and for the float
 * rounding in the server's own refill arithmetic, which can leave a call
 * landing at exactly one second looking at 0.9999… tokens.
 */
const PACED_SPACING_MS = 1_100;

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

function messageOf(err: unknown): string {
  const m = (err as { message?: unknown } | null)?.message;
  return typeof m === 'string' ? m : String(err);
}

/**
 * How long to wait before retrying, or null when the failure is not a rate
 * limit and must not be retried at all.
 *
 * `retryAfter` (seconds) is the backend's `Retry-After` as the portal's fetch
 * layer surfaces it. When it is absent the delay doubles per attempt, so a
 * caller that never learns the real window still stops hammering.
 */
function rateLimitDelayMs(err: unknown, attempt: number): number | null {
  const e = err as { statusCode?: number; status?: number; retryAfter?: number } | null;
  if ((e?.statusCode ?? e?.status) !== 429) return null;
  const retryAfter = e?.retryAfter;
  if (typeof retryAfter === 'number' && retryAfter > 0) return retryAfter * 1_000;
  return Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
}

export async function runBulk(
  ids: string[],
  op: (id: string) => Promise<void>,
  opts: BulkRunOptions = {},
): Promise<BulkRunResult> {
  const concurrency = Math.max(1, opts.concurrency ?? DEFAULT_CONCURRENCY);
  const maxRetries = opts.maxRetries ?? DEFAULT_MAX_RETRIES;
  const sleep = opts.sleep ?? defaultSleep;
  const now = opts.now ?? Date.now;
  const onProgress = opts.onProgress;

  const succeeded: string[] = [];
  const failed: { id: string; message: string }[] = [];
  let cursor = 0;

  /**
   * The earliest moment the next call may go out — shared by every worker,
   * and `null` until the server first says "too many". While it is null the
   * run is unpaced and `reserveSlot` costs nothing, which is what keeps a
   * selection the bucket can absorb as fast as it always was.
   */
  let nextSlotAt: number | null = null;

  /**
   * Claim this call's place in the queue and report how long to wait for it.
   *
   * Reserving BEFORE waiting is the whole trick: four workers each take a
   * DIFFERENT slot, one spacing apart, instead of all sleeping the same
   * backoff and waking onto the same single token. Synchronous by design —
   * an unpaced run must still call `op` without an intervening microtask.
   */
  function reserveSlot(): number {
    if (nextSlotAt === null) return 0;
    const t = now();
    const waitMs = nextSlotAt - t;
    nextSlotAt = Math.max(t, nextSlotAt) + PACED_SPACING_MS;
    return waitMs;
  }

  /** A 429: hold the whole run off for `delayMs`, not just this worker. */
  function holdOff(delayMs: number): void {
    const until = now() + delayMs;
    nextSlotAt = nextSlotAt === null ? until : Math.max(nextSlotAt, until);
  }

  async function worker(): Promise<void> {
    for (;;) {
      const index = cursor;
      cursor += 1;
      if (index >= ids.length) return;
      const id = ids[index];

      for (let attempt = 0; ; attempt += 1) {
        const waitMs = reserveSlot();
        if (waitMs > 0) await sleep(waitMs);
        try {
          await op(id);
          succeeded.push(id);
          break;
        } catch (err) {
          const delayMs = attempt < maxRetries ? rateLimitDelayMs(err, attempt) : null;
          if (delayMs === null) {
            failed.push({ id, message: messageOf(err) });
            break;
          }
          // The wait itself happens in `reserveSlot` on the next pass, so a
          // backoff and the pacing gap can't stack into a double wait.
          holdOff(delayMs);
        }
      }
      onProgress?.(succeeded.length + failed.length, ids.length);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(concurrency, ids.length) }, () => worker()),
  );

  return { succeeded, failed };
}
